import { LoaderCircle, RefreshCw, WifiOff } from 'lucide-react';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/Button';
import type { ApiError } from '@/lib/api';

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-muted px-6 pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] text-center">
      {children}
    </div>
  );
}

/** Shown while `/auth/me` answers at start. */
export function SplashScreen() {
  return (
    <Centered>
      <img src="/favicon.svg" alt="" className="size-16 rounded-2xl" />
      <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
        <LoaderCircle className="size-4 animate-spin" aria-hidden />
        Caricamento…
      </p>
    </Centered>
  );
}

/**
 * Friendly page when the server cannot be reached and no user is known on this device yet (first
 * access needs the network). With a known user the app works offline and this is not shown.
 */
export function OfflineScreen({
  error,
  onRetry,
}: {
  error: ApiError;
  onRetry: () => Promise<unknown>;
}) {
  const [busy, setBusy] = useState(false);
  const network = error.isNetworkError;
  return (
    <Centered>
      <WifiOff className="size-12 text-muted-foreground" aria-hidden />
      <div className="max-w-sm space-y-2">
        <h1 className="text-xl font-bold text-primary">
          {network ? 'Connessione assente' : 'Servizio non disponibile'}
        </h1>
        <p className="text-sm text-muted-foreground">
          {network
            ? 'Per il primo accesso su questo dispositivo serve la rete. Controlla il Wi-Fi o la rete mobile e riprova.'
            : 'Il server non risponde in questo momento. Riprova tra poco; se il problema continua avvisa un responsabile FMF.'}
        </p>
      </div>
      <Button
        size="lg"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            await onRetry();
          } finally {
            setBusy(false);
          }
        }}
      >
        <RefreshCw className={busy ? 'animate-spin' : undefined} />
        Riprova
      </Button>
    </Centered>
  );
}
